/**
 * Post-onboarding nudge — Android only — asking the user to exempt
 * this app from battery optimization (+ enable OEM "Autostart" when a
 * matching settings screen exists). Separate from PermissionsPage.jsx
 * on purpose: that screen is under a strict Apple Guideline 5.1.1(iv)
 * constraint (no skip/dismiss button, must always lead straight to an
 * OS permission prompt) — mixing an Android-only, non-"permission"
 * Settings deep-link into that reviewed flow would both violate the
 * spirit of that rule and simply not apply on iOS. This is a normal
 * dismissible card instead, shown once real notifications matter
 * (after login, same mount point as StickyCart), not blocking anything.
 *
 * Root cause this exists for: bulk/order push notifications only
 * arriving while the app stayed alive in the recents list, never once
 * fully killed — traced to Android OEM skins throttling background FCM
 * delivery for apps that aren't exempted from battery optimization.
 * See scripts/_build_common.py:patch_battery_optimization_plugin for
 * the native plugin this card drives.
 */
import { useEffect, useState } from "react";
import { BatteryCharging, X } from "lucide-react";
import {
    isIgnoringBatteryOptimizations,
    requestBatteryOptimizationExemption,
    openAutostartSettings,
} from "../lib/capacitor";
import { useAuth } from "../contexts/AuthContext";
import { hasCompletedPermissions } from "../pages/PermissionsPage";

const DISMISS_KEY = "bizil_battery_nudge_dismissed";

export default function BatteryOptimizationNudge() {
    const { user } = useAuth();
    const [show, setShow] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            if (!user) return;
            // Same App Store Guideline 5.1.1(iv) gating ZoneOfflineBanner
            // uses — never show any post-onboarding UI until the user has
            // actually been through (or explicitly finished) the reviewed
            // permissions screen.
            if (!hasCompletedPermissions()) return;
            try {
                if (localStorage.getItem(DISMISS_KEY)) return;
            } catch { /* ignore — fail open, worst case shows once more */ }
            const ignoring = await isIgnoringBatteryOptimizations();
            // ``isIgnoringBatteryOptimizations`` resolves true on web/iOS
            // (nothing to exempt there) — this naturally no-ops the card
            // on every platform except un-exempted Android installs.
            if (alive && !ignoring) setShow(true);
        })();
        return () => { alive = false; };
    }, [user]);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
        setShow(false);
    };

    const allow = async () => {
        await requestBatteryOptimizationExemption();
        // The OS dialog (or its fallback Settings page) takes over from
        // here — we don't know the outcome until the user returns, so
        // just dismiss the card rather than re-poll indefinitely.
        dismiss();
    };

    const autostart = async () => {
        await openAutostartSettings();
        dismiss();
    };

    if (!show) return null;

    return (
        <div
            data-testid="battery-optimization-nudge"
            // Top banner (like ZoneOfflineBanner), offset further down so
            // the two don't stack on top of each other on the rare
            // overlap — a saved address in an inactive zone AND a
            // not-yet-exempted Android install at the same time.
            // ``StickyCart``/bottom-nav own the bottom band, so this
            // stays clear of those too.
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 5rem)", maxWidth: "calc(var(--shell-width) - 24px)" }}
            className="fixed left-1/2 -translate-x-1/2 z-40 w-[calc(100%-1.5rem)] bg-card border border-border rounded-2xl shadow-2xl p-4 animate-in slide-in-from-top"
        >
            <div className="flex items-start gap-3">
                <span className="shrink-0 w-9 h-9 rounded-xl bg-amber-500/15 text-amber-600 inline-flex items-center justify-center">
                    <BatteryCharging size={18} />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">Get reliable order alerts</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                        Some phones stop notifications after the app is closed. Allow it to run in the background so you never miss an order update.
                    </p>
                </div>
                <button
                    onClick={dismiss}
                    data-testid="battery-nudge-dismiss"
                    aria-label="Dismiss"
                    className="shrink-0 p-1 -m-1 text-muted-foreground"
                ><X size={16} /></button>
            </div>
            <div className="mt-3 flex gap-2">
                <button
                    onClick={autostart}
                    data-testid="battery-nudge-autostart"
                    className="flex-1 bg-muted hover:bg-muted/80 rounded-xl py-2 text-xs font-bold"
                >Autostart settings</button>
                <button
                    onClick={allow}
                    data-testid="battery-nudge-allow"
                    className="flex-1 bg-primary text-primary-foreground rounded-xl py-2 text-xs font-bold"
                >Allow</button>
            </div>
        </div>
    );
}
